import { Check } from 'lucide-react'
import { useId, type ComponentProps, type ReactNode } from 'react'
import { cx } from './cx'

type ToggleProps = Omit<ComponentProps<'input'>, 'type' | 'className' | 'children'> & {
  label: ReactNode
  /** Second line under the label. */
  description?: ReactNode
  className?: string
}

// Unchecked borders use ink-3 (≥ 3:1 non-text contrast); the hairline tokens are too faint to identify a control.
const box =
  'peer size-[18px] shrink-0 cursor-pointer appearance-none rounded-[5px] border-[1.5px] border-ink-3 bg-surface ' +
  'transition-colors duration-150 checked:border-ink checked:bg-ink ' +
  'disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-offset-2'

/** Native checkbox with label; the whole row (≥ 44 px) is the hit area. */
export function Checkbox({ label, description, className, id, ...props }: ToggleProps) {
  const auto = useId()
  const inputId = id ?? auto
  const descId = description ? `${inputId}-desc` : undefined
  return (
    <div className={cx('flex min-h-11 items-start gap-3 py-2.5 pointer-fine:min-h-9', className)}>
      <span className="relative mt-0.5 grid">
        <input type="checkbox" id={inputId} aria-describedby={descId} className={cx(box, 'col-start-1 row-start-1')} {...props} />
        <Check
          aria-hidden="true"
          strokeWidth={3}
          className="pointer-events-none col-start-1 row-start-1 size-3 place-self-center text-ink-inverse opacity-0 peer-checked:opacity-100"
        />
      </span>
      <span className="flex flex-col">
        <label htmlFor={inputId} className="cursor-pointer text-body text-ink select-none">
          {label}
        </label>
        {description && (
          <span id={descId} className="text-small text-ink-3">
            {description}
          </span>
        )}
      </span>
    </div>
  )
}

/** On/off setting that applies immediately (role="switch"). For form submits use Checkbox. */
export function Switch({ label, description, className, id, checked, ...props }: ToggleProps) {
  const auto = useId()
  const inputId = id ?? auto
  const descId = description ? `${inputId}-desc` : undefined
  return (
    <div className={cx('flex min-h-11 items-center justify-between gap-4 py-2', className)}>
      <span className="flex flex-col">
        <label htmlFor={inputId} className="cursor-pointer text-body text-ink select-none">
          {label}
        </label>
        {description && (
          <span id={descId} className="text-small text-ink-3">
            {description}
          </span>
        )}
      </span>
      <span className="relative inline-flex shrink-0">
        <input
          type="checkbox"
          role="switch"
          id={inputId}
          checked={checked}
          // Native checkboxes expose their state anyway; explicit for AT that only reads aria-checked on switches.
          aria-checked={checked}
          aria-describedby={descId}
          className={cx(
            'peer h-6 w-10 cursor-pointer appearance-none rounded-full bg-ink-3/45 transition-colors duration-150',
            'checked:bg-accent disabled:cursor-not-allowed disabled:opacity-45',
          )}
          {...props}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow-[0_1px_2px_rgb(0_0_0/0.2)] transition-transform duration-150 peer-checked:translate-x-4"
        />
      </span>
    </div>
  )
}

export type ChoiceOption<V extends string> = {
  value: V
  label: ReactNode
  description?: ReactNode
  disabled?: boolean
}

type RadioGroupProps<V extends string> = {
  label: ReactNode
  /** Hide the legend visually (keep it for screen readers). */
  hideLabel?: boolean
  name?: string
  value: V | undefined
  onChange: (value: V) => void
  options: ChoiceOption<V>[]
  orientation?: 'vertical' | 'horizontal'
  className?: string
}

/** Native radios in a fieldset: arrow keys move selection for free. */
export function RadioGroup<V extends string>({
  label,
  hideLabel,
  name,
  value,
  onChange,
  options,
  orientation = 'vertical',
  className,
}: RadioGroupProps<V>) {
  const auto = useId()
  const groupName = name ?? auto
  return (
    <fieldset className={cx('min-w-0', className)}>
      <legend className={cx('mb-1 text-small font-medium text-ink', hideLabel && 'sr-only')}>{label}</legend>
      <div className={cx('flex', orientation === 'vertical' ? 'flex-col' : 'flex-wrap gap-x-6')}>
        {options.map((o) => {
          const id = `${groupName}-${o.value}`
          return (
            <div key={o.value} className="flex min-h-11 items-start gap-3 py-2.5 pointer-fine:min-h-9">
              <input
                type="radio"
                id={id}
                name={groupName}
                value={o.value}
                checked={value === o.value}
                disabled={o.disabled}
                onChange={() => onChange(o.value)}
                className={cx(
                  'mt-0.5 size-[18px] shrink-0 cursor-pointer appearance-none rounded-full border-[1.5px] border-ink-3 bg-surface',
                  'transition-[border-color,border-width] duration-150 checked:border-[5.5px] checked:border-ink',
                  'disabled:cursor-not-allowed disabled:opacity-45',
                )}
              />
              <span className="flex flex-col">
                <label htmlFor={id} className="cursor-pointer text-body text-ink select-none">
                  {o.label}
                </label>
                {o.description && <span className="text-small text-ink-3">{o.description}</span>}
              </span>
            </div>
          )
        })}
      </div>
    </fieldset>
  )
}

type SegmentedProps<V extends string> = {
  /** Accessible group name (e.g. "Ansicht"). */
  label: string
  value: V
  onChange: (value: V) => void
  options: { value: V; label: ReactNode; disabled?: boolean }[]
  size?: 'sm' | 'md'
  block?: boolean
  className?: string
}

/** Compact either/or switcher. Native radios → radiogroup semantics and arrow-key navigation. */
export function SegmentedControl<V extends string>({
  label,
  value,
  onChange,
  options,
  size = 'md',
  block,
  className,
}: SegmentedProps<V>) {
  const name = useId()
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx(
        // Never wider than its column: on the narrowest phones it scrolls inside itself, the page stays put. Only
        // there, as the scroll box would clip the focus ring by a pixel.
        'inline-flex max-w-full gap-0.5 rounded-control bg-bg-hover p-0.5 max-sm:overflow-x-auto max-sm:[scrollbar-width:none]',
        block && 'flex w-full',
        className,
      )}
    >
      {options.map((o) => (
        <label
          key={o.value}
          className={cx(
            'relative inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-[6px] font-medium text-ink-2 transition-colors duration-150 select-none',
            size === 'sm' ? 'h-7 px-2.5 text-small' : 'h-10 px-2 text-small min-[400px]:px-3.5 pointer-fine:h-8',
            'hover:text-ink has-checked:bg-surface-raised has-checked:text-ink has-checked:shadow-[0_1px_2px_rgb(0_0_0/0.08),0_0_0_0.5px_rgb(0_0_0/0.04)]',
            'has-focus-visible:outline-2 has-focus-visible:outline-offset-1 has-focus-visible:outline-accent',
            'has-disabled:cursor-not-allowed has-disabled:opacity-45',
          )}
        >
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            disabled={o.disabled}
            onChange={() => onChange(o.value)}
            className="sr-only"
          />
          {o.label}
        </label>
      ))}
    </div>
  )
}
